import { useState, useEffect } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Feedback Activity Component
 * 5-question feedback format with emoji ratings
 *
 * Questions:
 * Q1: Topic Relevance - "How relevant and useful was this topic for your role?"
 * Q2: Trainer Effectiveness - "How effectively did the trainer help you understand and engage with this topic?"
 * Q3: Confidence to Apply - "How confident do you feel about applying this topic to overcome real work challenges?"
 * Q4: Learning Advocacy - "How likely are you to apply this learning and recommend it to others?"
 * Q5: Challenge Resolution - "To what extent did this topic address a real challenge you are currently facing?"
 */

// The 5 standard feedback questions
const FEEDBACK_QUESTIONS = [
  {
    key: 'topicRelevance',
    question: 'How relevant and useful was this topic for your role?',
    shortLabel: 'Topic Relevance',
  },
  {
    key: 'trainerEffectiveness',
    question: 'How effectively did the trainer help you understand and engage with this topic?',
    shortLabel: 'Trainer Effectiveness',
  },
  {
    key: 'confidenceToApply',
    question: 'How confident do you feel about applying this topic to overcome real work challenges?',
    shortLabel: 'Confidence to Apply',
  },
  {
    key: 'learningAdvocacy',
    question: 'How likely are you to apply this learning and recommend it to others?',
    shortLabel: 'Learning Advocacy',
  },
  {
    key: 'challengeResolution',
    question: 'To what extent did this topic address a real challenge you are currently facing?',
    shortLabel: 'Challenge Resolution',
  },
];

// Emoji rating options (1-5 scale)
const EMOJI_OPTIONS = [
  { value: 1, emoji: '😞', label: 'Poor' },
  { value: 2, emoji: '😑', label: 'Fair' },
  { value: 3, emoji: '🙂', label: 'Okay' },
  { value: 4, emoji: '😄', label: 'Good' },
  { value: 5, emoji: '🤩', label: 'Excellent' },
];

const FeedbackActivity = ({
  activity,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  onSubmit,
  onBackToList,
  showResults = false,
  userFeedback = null,
  isActivityEnded = false,
}) => {
  // State for the 5 question ratings
  const [ratings, setRatings] = useState({
    topicRelevance: userFeedback?.topicRelevance || 0,
    trainerEffectiveness: userFeedback?.trainerEffectiveness || 0,
    confidenceToApply: userFeedback?.confidenceToApply || 0,
    learningAdvocacy: userFeedback?.learningAdvocacy || 0,
    challengeResolution: userFeedback?.challengeResolution || 0,
  });
  const [feedbackComment, setFeedbackComment] = useState(userFeedback?.comment || '');
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Update state when userFeedback prop changes
  useEffect(() => {
    if (userFeedback) {
      setRatings({
        topicRelevance: userFeedback.topicRelevance || 0,
        trainerEffectiveness: userFeedback.trainerEffectiveness || 0,
        confidenceToApply: userFeedback.confidenceToApply || 0,
        learningAdvocacy: userFeedback.learningAdvocacy || 0,
        challengeResolution: userFeedback.challengeResolution || 0,
      });
      if (userFeedback.comment) setFeedbackComment(userFeedback.comment);
    }
  }, [userFeedback]);

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress
  const maxTime = activity?.timerEnabled === true
    ? (activity?.timeLimit || (activity?.duration ? activity.duration * 60 : null) || activity?.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;

  // Handle rating selection
  const handleRatingSelect = (questionKey, value) => {
    setRatings((prev) => ({ ...prev, [questionKey]: value }));
    // Auto-advance to next question after a short delay
    if (currentQuestion < FEEDBACK_QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 300);
    }
  };

  // Check if all questions are answered
  const allQuestionsAnswered = Object.values(ratings).every((r) => r > 0);

  // Calculate average rating for display
  const averageRating = allQuestionsAnswered
    ? (Object.values(ratings).reduce((sum, r) => sum + r, 0) / 5).toFixed(1)
    : null;

  // Get emoji for rating value
  const getEmojiForRating = (value) => EMOJI_OPTIONS.find((e) => e.value === value)?.emoji || '';

  const handleSubmit = () => {
    if (allQuestionsAnswered) {
      // Calculate legacy rating (average) for backwards compatibility
      const avgRating = Math.round(Object.values(ratings).reduce((sum, r) => sum + r, 0) / 5);
      const avgEmoji = getEmojiForRating(avgRating);

      onSubmit({
        // New 5-question format
        topicRelevance: ratings.topicRelevance,
        trainerEffectiveness: ratings.trainerEffectiveness,
        confidenceToApply: ratings.confidenceToApply,
        learningAdvocacy: ratings.learningAdvocacy,
        challengeResolution: ratings.challengeResolution,
        comment: feedbackComment,
        // Legacy fields for backwards compatibility
        rating: avgRating,
        emoji: avgEmoji,
      });
    }
  };

  // Activity Ended but Not Responded View
  if (isActivityEnded && !showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                  <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                  <span className="text-xs font-medium text-neutral-600">Ended</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">FEEDBACK</h2>
          </div>
        </div>

        {/* Not Attended Message */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-neutral-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-2">Activity Ended</h2>
            <p className="text-neutral-600 mb-4">You didn't attend this feedback activity.</p>
            <p className="text-sm text-neutral-500">This activity has already ended, so you can no longer submit a response.</p>
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-neutral-600 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (showResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isActivityEnded ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                    <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                    <span className="text-xs font-medium text-neutral-600">Ended</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                )}
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">FEEDBACK</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Success Message */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-green-800">Feedback Submitted!</h2>
                <p className="text-sm sm:text-base text-green-700">
                  {isActivityEnded
                    ? 'This activity has ended. Your feedback has been recorded.'
                    : 'Thank you for sharing your thoughts'}
                </p>
              </div>
            </div>
          </div>

          {/* Feedback Summary */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-4 sm:mb-6">{activity.title}</h3>

            {/* Average Rating */}
            {averageRating && (
              <div className="p-4 bg-primary-50 rounded-xl mb-4 text-center">
                <p className="text-sm text-primary-600 font-medium mb-1">Your Average Rating</p>
                <p className="text-3xl font-bold text-primary-700">{averageRating}</p>
                <p className="text-xs text-primary-500 mt-1">out of 5</p>
              </div>
            )}

            {/* Individual Ratings */}
            <div className="space-y-3">
              {FEEDBACK_QUESTIONS.map((q) => (
                <div key={q.key} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                  <span className="text-sm text-neutral-700 flex-1">{q.shortLabel}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{getEmojiForRating(ratings[q.key])}</span>
                    <span className="text-sm font-semibold text-neutral-900 w-6 text-right">{ratings[q.key]}</span>
                  </div>
                </div>
              ))}
            </div>

            {feedbackComment && (
              <div className="mt-4 pt-4 border-t border-neutral-200">
                <p className="text-xs sm:text-sm text-neutral-600 mb-1">Your comment:</p>
                <p className="text-sm sm:text-base text-neutral-900 italic">"{feedbackComment}"</p>
              </div>
            )}
          </div>

          {/* Participation Stats */}
          {(() => {
            const serverResponseCount = activity.responseCount || 0;
            const responseCount = allQuestionsAnswered ? Math.max(serverResponseCount, 1) : serverResponseCount;
            const totalParticipants = activity.totalParticipants || responseCount || 1;
            const percentage = totalParticipants > 0 ? Math.round((responseCount / totalParticipants) * 100) : 0;

            return (
              <div className="p-3 sm:p-4 bg-neutral-50 rounded-xl">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-2 text-xs sm:text-sm">
                  <span className="text-neutral-600">
                    {responseCount} of {totalParticipants} participants submitted feedback
                  </span>
                  <span className="font-semibold text-primary-600">{percentage}%</span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Input View - 5 Questions Format
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">FEEDBACK</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left Side - Feedback Form */}
          <div className="flex-1 order-2 lg:order-1">
            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3 sm:mb-4 leading-tight">
              {activity.title || 'Session Feedback'}
            </h1>

            <p className="text-base sm:text-lg text-neutral-600 mb-6 sm:mb-8">
              Please rate your experience on each of the following aspects.
            </p>

            {/* Progress Indicator */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-neutral-600">
                  {Object.values(ratings).filter((r) => r > 0).length} of 5 questions answered
                </span>
              </div>
              <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${(Object.values(ratings).filter((r) => r > 0).length / 5) * 100}%` }}
                />
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-4 mb-6">
              {FEEDBACK_QUESTIONS.map((q, index) => (
                <div
                  key={q.key}
                  className={`bg-neutral-50 rounded-xl p-4 sm:p-5 transition-all cursor-pointer ${
                    currentQuestion === index ? 'ring-2 ring-primary-500 bg-white' : ''
                  } ${ratings[q.key] > 0 ? 'border-l-4 border-green-500' : ''}`}
                  onClick={() => setCurrentQuestion(index)}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        ratings[q.key] > 0 ? 'bg-green-500 text-white' : 'bg-neutral-200 text-neutral-600'
                      }`}
                    >
                      {ratings[q.key] > 0 ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <span className="text-xs font-bold">{index + 1}</span>
                      )}
                    </div>
                    <p className="text-sm sm:text-base text-neutral-800 font-medium flex-1">{q.question}</p>
                  </div>

                  {/* Emoji Rating Row */}
                  <div className="flex justify-center gap-1 sm:gap-2">
                    {EMOJI_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRatingSelect(q.key, option.value);
                        }}
                        className={`flex flex-col items-center p-2 sm:p-3 rounded-xl border-2 transition-all ${
                          ratings[q.key] === option.value
                            ? 'border-primary-500 bg-primary-50 scale-105'
                            : 'border-transparent hover:border-neutral-300 bg-white'
                        }`}
                      >
                        <span className="text-xl sm:text-2xl">{option.emoji}</span>
                        <span className="text-[9px] sm:text-xs text-neutral-500 mt-1 hidden sm:block">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Comment Input */}
            <div className="bg-neutral-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
              <h3 className="font-semibold text-neutral-900 mb-3 sm:mb-4">Additional comments (optional)</h3>
              <textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Share any additional thoughts..."
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 text-neutral-900 bg-white border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 resize-none transition-all"
              />
              <p className="text-xs text-neutral-500 mt-2">{feedbackComment.length}/500 characters</p>
            </div>

            {/* Submit Button */}
            <div className="mb-8">
              <button
                onClick={handleSubmit}
                disabled={!allQuestionsAnswered}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                {allQuestionsAnswered ? 'Submit Feedback' : `Answer all ${5 - Object.values(ratings).filter((r) => r > 0).length} remaining questions`}
              </button>
            </div>
          </div>

          {/* Right Side - Timer & Info */}
          <div className="w-full lg:w-64 flex-shrink-0 order-1 lg:order-2">
            <div className="lg:sticky lg:top-8">
              {/* Circular Timer */}
              {timerEnabled && (
              <div className="flex flex-row lg:flex-col items-center gap-4 lg:gap-0 mb-6 lg:mb-8 p-4 lg:p-0 bg-neutral-50 lg:bg-transparent rounded-xl lg:rounded-none">
                <div className="relative w-20 h-20 lg:w-24 lg:h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#ff8a00"
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - timeRemaining / maxTime)}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg lg:text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 lg:mt-2">Time Remaining</span>
              </div>
              )}

              {/* Progress Summary - Desktop */}
              <div className="hidden lg:block bg-primary-50 rounded-xl p-4 border border-primary-100">
                <h4 className="font-semibold text-primary-900 mb-3">Your Progress</h4>
                <div className="space-y-2">
                  {FEEDBACK_QUESTIONS.map((q, index) => (
                    <div key={q.key} className="flex items-center justify-between text-sm">
                      <span className="text-primary-700">Q{index + 1}</span>
                      {ratings[q.key] > 0 ? (
                        <span className="text-lg">{getEmojiForRating(ratings[q.key])}</span>
                      ) : (
                        <span className="text-neutral-400">-</span>
                      )}
                    </div>
                  ))}
                  <div className="pt-2 border-t border-primary-200 flex items-center justify-between text-sm">
                    <span className="text-primary-700">Comment</span>
                    <span className="text-neutral-500">{feedbackComment.length > 0 ? 'Added' : 'Optional'}</span>
                  </div>
                </div>
              </div>

              {/* Anonymous Notice */}
              <div className="hidden lg:flex mt-4 items-center gap-2 text-sm text-neutral-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span>Feedback is anonymous</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackActivity;
